/**
 * Gallery model — the PURE data layer behind the `/` gallery: a unified
 * {@link GalleryItem} that folds LLM creations and the hardcoded native games
 * into one shape, plus the sort/filter/search functions the Gallery component
 * (and its tests) drive. Keeping this pure (no React, no Firestore) makes the
 * gallery's behavior directly unit-testable.
 *
 * Trending order uses the shared {@link score} from @djgg/creations so the
 * portal and any server-side ranking agree. Native cards DISPLAY the real
 * counters from their creation docs (kind='native', merged in by the Gallery),
 * but keep their evergreen ORDERING: under every sort they trail creations
 * (stable, by title) — they are catalog entries, not fresh publishes.
 */
import type { Creation, CreationRating, CreationStyle, DemoRefV1, ListSort } from '@djgg/creations';
import { activeDemoRef, score, isBlocked, isShowcase, LIKE_WEIGHT, NEUTRAL_APPEAL, PLAY_WEIGHT } from '@djgg/creations';
import type { NativeGame } from './nativeGames.js';
import { playUrl } from '../services/playUrl.js';
import { isAudioKind } from '../studio/audioBrief.js';

/** Add the platform's passive attract-mode flag without dropping any existing
 * query or hash. Kept pure so native and creation projections cannot drift. */
export function demoUrl(url: string): string {
  const absolute = /^[a-z][a-z0-9+.-]*:/i.test(url);
  const parsed = new URL(url, 'https://djgg.invalid');
  parsed.searchParams.set('demo', '1');
  return absolute ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/** Alternate recent releases and popular games, shuffling small ranked groups
 * so every visit has variety without burying new games or proven favorites.
 * The caller keeps one seed per visit and retains the order across live updates. */
export function sortExploreItems(items: GalleryItem[], seed: number): GalleryItem[] {
  let state = seed >>> 0;
  const random = (): number => {
    state += 0x6d2b79f5;
    let value = Math.imul(state ^ (state >>> 15), state | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 0x1_0000_0000;
  };
  const shuffleGroups = (ranked: GalleryItem[]): GalleryItem[] => {
    // Each group of five stays ahead of the next group, retaining the ranking
    // signal while giving several games a chance to lead on a fresh visit.
    for (let start = 0; start < ranked.length; start += 5) {
      for (let i = Math.min(start + 4, ranked.length - 1); i > start; i--) {
        const j = start + Math.floor(random() * (i - start + 1));
        [ranked[i], ranked[j]] = [ranked[j], ranked[i]];
      }
    }
    return ranked;
  };
  const popularity = (item: GalleryItem): number =>
    item.likes * LIKE_WEIGHT + item.plays * PLAY_WEIGHT;
  const previewTier = (item: GalleryItem): number => {
    if (item.previewKind === 'video') return 2;
    if (item.previewKind === 'iframe') return 1;
    return 0;
  };
  const popular = shuffleGroups(items.filter((item) => popularity(item) > 0).sort((a, b) => {
    const engagement = popularity(b) - popularity(a);
    if (engagement !== 0) return engagement;
    const preview = previewTier(b) - previewTier(a);
    if (preview !== 0) return preview;
    return a.title.localeCompare(b.title);
  }));
  const recent = shuffleGroups(items.filter((item) => item.kind === 'creation').sort(
    (a, b) => itemCreated(b) - itemCreated(a) || a.title.localeCompare(b.title),
  ));
  const queues = random() < 0.5 ? [recent, popular] : [popular, recent];
  const positions = [0, 0];
  const seen = new Set<string>();
  const mixed: GalleryItem[] = [];
  const add = (item: GalleryItem) => {
    if (seen.has(item.id)) return;
    seen.add(item.id);
    mixed.push(item);
  };
  while (positions.some((position, i) => position < queues[i].length)) {
    queues.forEach((queue, i) => {
      while (positions[i] < queue.length && seen.has(queue[positions[i]].id)) positions[i]++;
      if (positions[i] < queue.length) add(queue[positions[i]++]);
    });
  }
  // Unplayed native games have no release date or engagement yet, but remain
  // discoverable after the ranked mix, including in a fresh empty catalogue.
  shuffleGroups([...items]).forEach(add);
  return mixed;
}

/** A card in the gallery — either a creation or a native game. */
export type GalleryItem =
  | {
      kind: 'creation';
      id: string;
      playUrl: string;
      previewUrl?: string;
      previewKind?: 'video' | 'iframe';
      previewProbeUrl?: string;
      demoRef?: DemoRefV1;
      titleOverlayUrl?: string;
      multiplayer: boolean;
      attachedAssetCount: number;
      storyAssetUrls?: string[];
      slug: string;
      title: string;
      description: string;
      style: CreationStyle;
      creatorHandle: string;
      likes: number;
      views: number;
      distinctViewers: number;
      plays: number;
      /** Diverged public forks of this game (0 for non-forked games). */
      forks: number;
      version: number;
      updatedAt: number;
      /** The game's quality RATING (see @djgg/creations `rating/evaluate.ts`) —
       *  playability from the rendered audit fused with the reviewer's appeal
       *  score. Undefined = never rated, which means UNKNOWN, not bad: the
       *  ranking treats it as average and the showcase mark never fires on it. */
      rating?: CreationRating;
      /** When a play last BEGAN on this game (0 = never played) — the "Last
       *  played" sort key. Server-owned on the doc; it advances on an engaged
       *  play or on the leading edge of a presence beat, and NOT on content
       *  edits, so it says something `updatedAt` cannot: this game is being
       *  played, whether or not anyone is still working on it. */
      lastPlayed: number;
      /** When the game came into being — the creation doc's birth, which is the
       *  moment its v1 build was kicked off (the wizard mints the doc and
       *  enqueues v1 in one flow; in prod the two stamps sit under a minute
       *  apart). The "Last created" sort's FALLBACK key ({@link publishedAt}
       *  is the primary), and the only origin stamp the gallery has locally:
       *  version docs are not part of this subscription. */
      createdAt: number;
      /** The set-once FIRST-PUBLISH stamp (`publishedAt`) — the "Last created"
       *  sort's primary key, so a game published long after its birth debuts
       *  fairly at its publish moment instead of buried at its creation date.
       *  Grandfathered rows carry `publishedAt === createdAt` (migration 0051);
       *  absent only on pre-backfill data, where createdAt stands in. */
      publishedAt?: number;
      status: Creation['status'];
      /** Genuinely PLAYABLE — an active version with a bundle exists. Decoupled
       *  from build progress: a live game mid-iteration (`buildState:'building'`)
       *  is still playable, so it stays in the gallery. Draft / never-built
       *  (truly failed) creations are not playable and are excluded. */
      playable: boolean;
      creatorUid: string;
      /** Optional 16:9 cover art; card falls back to a gradient when absent. */
      coverUrl?: string;
      /** Optional static gameplay poster used when cover art is absent. */
      posterUrl?: string;
      /** Editors' Pick marks — drives the curated row + the card laurel.
       *  `everSelected` = the permanent "Official Selection" laurel; `current`
       *  = in the active weekly list (ordered by `currentRank`). Absent when
       *  the game was never selected. */
      editorsPick?: {
        everSelected: boolean;
        current: boolean;
        currentRank?: number;
        selectionCount: number;
        /** Latest edition label (e.g. `2026-W29`) for the card/banner. */
        edition?: string;
      };
    }
  | {
      kind: 'native';
      id: string;
      playUrl: string;
      previewUrl?: string;
      previewKind?: 'video' | 'iframe';
      previewProbeUrl?: string;
      demoRef?: DemoRefV1;
      titleOverlayUrl?: string;
      multiplayer: boolean;
      attachedAssetCount: number;
      storyAssetUrls?: string[];
      slug: string;
      title: string;
      description: string;
      style: CreationStyle;
      creatorHandle: string;
      /** Real social counters from the native's creation doc — 0 until the doc
       *  arrives. The card always renders the stats row, zeros included. */
      likes: number;
      views: number;
      plays: number;
      /** Optional 16:9 cover art; card falls back to a gradient when absent. */
      coverUrl?: string;
      /** Optional static gameplay poster used when cover art is absent. */
      posterUrl?: string;
      /** Editors' Pick marks — natives are eligible picks too (from their
       *  `native-<slug>` doc). Same shape as the creation variant. */
      editorsPick?: {
        everSelected: boolean;
        current: boolean;
        currentRank?: number;
        selectionCount: number;
        edition?: string;
      };
    };

/** Project the doc's editorsPick marks onto the compact gallery-item shape (the
 *  latest edition surfaced for the card/banner). Shared by both item kinds. */
function editorsPickView(
  ep: Creation['editorsPick'],
): Extract<GalleryItem, { kind: 'creation' }>['editorsPick'] {
  return ep
    ? {
        everSelected: ep.everSelected,
        current: ep.current,
        currentRank: ep.currentRank,
        selectionCount: ep.selectionCount,
        edition: ep.editions?.[ep.editions.length - 1],
      }
    : undefined;
}

/** Legacy version URLs remain valid for ordinary play and recorded-preview
 * matching, but are never promoted to a DemoRef. */
function pinnedLegacyBundle(c: Creation & { id: string }): string | undefined {
  const version = c.activeVersion ?? 0;
  if (!Number.isSafeInteger(version) || version <= 0 || !c.activeBundleUrl) return undefined;
  try {
    const url = new URL(c.activeBundleUrl);
    const parts = url.pathname.split('/').slice(1).map(decodeURIComponent);
    if (url.origin !== 'https://storage.googleapis.com' || url.username || url.password ||
      parts.length !== 5 || !parts[0] || parts[1] !== c.id || parts[2] !== String(version) ||
      parts[3] !== 'web' || parts[4] !== 'index.html') return undefined;
    return url.href;
  } catch { return undefined; }
}

/** Recorded reel artifacts must live in the active bundle's immutable version
 * directory. A copied legacy URL has no trustworthy version identity, even
 * when its extension is a video. Never let one override newer gameplay art.
 *
 * One checker for both halves of the reel: the clip and the still poster
 * recorded with it are written by the same build into the same directory, so
 * they must earn their place by the same rule. */
function pinnedVersionArtifact(
  c: Creation & { id: string },
  url: string | null | undefined,
  name: RegExp,
): string | undefined {
  if (!url || (!activeDemoRef(c) && !pinnedLegacyBundle(c))) return undefined;
  try {
    const bundle = new URL(c.activeBundleUrl!);
    const artifact = new URL(url);
    const parts = artifact.pathname.split('/').slice(1).map(decodeURIComponent);
    const bundleParts = bundle.pathname.split('/').slice(1).map(decodeURIComponent);
    if (artifact.origin !== bundle.origin || artifact.username || artifact.password ||
      parts.length !== 4 || parts.slice(0, 3).some((part, i) => part !== bundleParts[i]) ||
      !name.test(parts[3])) return undefined;
    return artifact.href;
  } catch { return undefined; }
}

/** Project a creation doc into a gallery item. */
export function creationToItem(c: Creation & { id: string }): GalleryItem {
  const id = c.id;
  const version = c.activeVersion ?? 0;
  // Attached music and SFX are not artwork: an audio URL in the trailer's
  // montage is a broken <img>, and they must not inflate the artworks count.
  const artworks = (c.assets ?? []).filter((asset) => !isAudioKind(asset.kind));
  const recordedPreview = pinnedVersionArtifact(c, c.previewUrl, /^preview\.(?:mp4|webm)$/i);
  // The still recorded WITH the clip. It is the video element's own poster, so
  // an autoplaying reel shows gameplay rather than a black box while the first
  // frame decodes. Only ever used alongside its clip.
  const recordedPoster = recordedPreview
    ? pinnedVersionArtifact(c, c.posterUrl, /^poster\.jpe?g$/i)
    : undefined;
  const exactDemoRef =
    version > 0 &&
    c.rating?.ratedVersion === version &&
    c.rating.playability !== null &&
    (c.rating.blockers?.length ?? 0) === 0
      ? activeDemoRef(c)
      : undefined;
  return {
    kind: 'creation',
    id,
    playUrl: playUrl(id),
    // 0036's follow-active recorded clip is used only with exact version
    // identity — `pinnedRecordedPreview` requires it to sit in the active
    // bundle's own immutable version directory, so a clip can never outlive the
    // gameplay it recorded.
    //
    // The RECORDED REEL WINS over the live demo frame when both exist. Both
    // show the same attract-mode gameplay of the same active version, but the
    // clip is a bounded, muted-by-default file of a couple of megabytes, where
    // the iframe boots an entire game bundle per card — WebGL context, audio
    // graph and all — in a phone feed that may have three cards alive. The
    // isolated bundle remains the preview for every version that has no reel
    // yet, and it is still used only when its active version has a successful
    // rendered playability audit, so older/menu-only games keep the animated
    // cover composition rather than advertising their main menu as gameplay.
    // (The iframe path takes its URL from `demoRef`, not from `previewUrl` —
    // see GameMediaStory — so `previewUrl` carries only the recorded clip.)
    previewUrl: recordedPreview,
    previewKind: recordedPreview ? 'video' : exactDemoRef ? 'iframe' : undefined,
    demoRef: recordedPreview ? undefined : exactDemoRef,
    posterUrl: recordedPoster,
    titleOverlayUrl:
      c.titleOverlayForTitle === c.title ? c.titleOverlayUrl : undefined,
    multiplayer: c.settings?.playModes?.some((mode) => mode === 'localVersus' || mode === 'onlineVersus') ?? false,
    attachedAssetCount: artworks.length,
    storyAssetUrls: artworks.slice(-3).map((asset) => asset.url),
    slug: c.slug,
    title: c.title,
    description: c.description,
    style: c.style,
    creatorHandle: c.creatorHandle,
    likes: c.likesCount,
    views: c.viewsCount,
    distinctViewers: c.distinctViewers ?? 0,
    plays: c.playsCount ?? 0,
    lastPlayed: c.lastPlayedAt,
    rating: c.rating,
    forks: c.forksCount ?? 0,
    version: c.latestVersion ?? 0,
    updatedAt: c.updatedAt,
    createdAt: c.createdAt,
    publishedAt: c.publishedAt ?? undefined,
    status: c.status,
    // Server-canonical playability (matches CreationsRepo.setBuildOutcome):
    // an active version that has a published bundle. buildState is irrelevant.
    playable: (c.activeVersion ?? 0) > 0 && !!c.activeBundleUrl,
    creatorUid: c.creatorUid,
    coverUrl: c.coverUrl,
    editorsPick: editorsPickView(c.editorsPick),
  };
}

/** Project a native game into a gallery item. Counters come from the native's
 *  creation doc (`native-<slug>` — the social surface natives have had since
 *  v0.7.0); an absent doc (fresh emulator) shows zeros. */
export function nativeToItem(g: NativeGame, doc?: Creation): GalleryItem {
  const play = g.link.kind === 'hosted' ? g.link.path : g.link.url;
  return {
    kind: 'native',
    id: `native-${g.slug}`,
    playUrl: play,
    previewUrl: g.link.kind === 'hosted' ? demoUrl(g.link.path) : undefined,
    previewKind: g.link.kind === 'hosted' ? 'iframe' : undefined,
    previewProbeUrl: g.link.kind === 'hosted' ? demoUrl(g.link.path) : undefined,
    titleOverlayUrl: g.titleOverlay,
    multiplayer: g.multiplayer,
    attachedAssetCount: 0,
    slug: g.slug,
    title: g.title,
    description: g.description,
    style: g.style,
    creatorHandle: g.author,
    likes: doc?.likesCount ?? 0,
    views: doc?.viewsCount ?? 0,
    plays: doc?.playsCount ?? 0,
    coverUrl: g.cover,
    posterUrl: g.poster,
    editorsPick: editorsPickView(doc?.editorsPick),
  };
}

/**
 * A card's "last updated" label, calendar-day based (local time) so the
 * boundaries flip at midnight rather than on a rolling 24h clock:
 *   - same day        → `today`
 *   - previous day    → `yesterday`
 *   - 2–6 days ago    → `N days ago`
 *   - 7–13 days ago   → `last week`
 *   - ≥14 days (or a future stamp) → `null` (the card shows nothing)
 * `now` is injected for deterministic tests.
 */
export function lastUpdatedLabel(updatedAt: number, now: number = Date.now()): string | null {
  const startOfDay = (ts: number) => {
    const d = new Date(ts);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  const days = Math.round((startOfDay(now) - startOfDay(updatedAt)) / 86_400_000);
  if (days < 0) return null;
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return 'last week';
  return null;
}

/** How long a first publish counts as fresh — the card's "NEW!" sticker window. */
const NEW_RELEASE_WINDOW_MS = 7 * 86_400_000;

/** True while the game's FIRST publish (`publishedAt`, migration 0051) is
 *  within the "NEW!" sticker window. Natives and never-published rows never
 *  qualify. `now` is injected for deterministic tests. */
export function isNewRelease(item: GalleryItem, now: number = Date.now()): boolean {
  return (
    item.kind === 'creation' &&
    item.publishedAt !== undefined &&
    now - item.publishedAt < NEW_RELEASE_WINDOW_MS
  );
}

/**
 * Gallery sort orders. The gallery owns its own sort semantics on top of the
 * repo's {@link ListSort} vocabulary: `newest` here means most-recently-UPDATED
 * ("Latest"), `created` is the gallery-only first-published order ("Last
 * created"), `iterated` ranks by how many versions a game has been through
 * ("Most iterated"), `lastPlayed` ranks by when a game was last PLAYED — the
 * one order driven by players rather than by creators — and `rated` ranks by
 * the quality RATING, the only order that knows whether a game actually works
 * (and the only one that drops games outright; see {@link isDemoted}). Every
 * key lives on the creation doc, so no extra query.
 */
export type GallerySort = ListSort | 'created' | 'iterated' | 'lastPlayed' | 'rated';

/** Filters applied to the gallery (all optional; empty = no filtering). */
export interface GalleryFilter {
  /** Restrict to a single style, or undefined for all. */
  style?: CreationStyle;
  /** Restrict to a creator handle (case-insensitive exact), or undefined. */
  creatorHandle?: string;
  /** Free-text search across title/description/creator. */
  search?: string;
  /** Sort order. */
  sort?: GallerySort;
  /** Whether native games are included (default true). */
  includeNative?: boolean;
}

/** True when `item` matches `filter` (style, creator, search). */
export function matchesFilter(item: GalleryItem, filter: GalleryFilter): boolean {
  if (filter.style && item.style !== filter.style) return false;
  if (
    filter.creatorHandle &&
    item.creatorHandle.toLowerCase() !== filter.creatorHandle.toLowerCase()
  ) {
    return false;
  }
  const q = filter.search?.trim().toLowerCase();
  if (q) {
    const hay = `${item.title} ${item.description} ${item.creatorHandle}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

/**
 * A creation item's trending score (native items score 0 so they trail). `now`
 * is injected for determinism.
 */
function itemScore(item: GalleryItem, now: number): number {
  if (item.kind !== 'creation') return -1;
  return score(item.likes, item.views, item.updatedAt, now, item.plays);
}

/** A creation item's recency key (native items are 0 so they trail). */
function itemRecency(item: GalleryItem): number {
  return item.kind === 'creation' ? item.updatedAt : 0;
}

/** A creation item's CREATION key — FIRST PUBLISHED, not last touched (natives
 *  are 0 so they trail, exactly as under every other sort). The documented
 *  "first published" order now genuinely keys off the set-once `publishedAt`
 *  stamp, so a late publish debuts at its publish moment; `createdAt` stands
 *  in only for pre-backfill rows that carry no stamp. */
function itemCreated(item: GalleryItem): number {
  return item.kind === 'creation' ? (item.publishedAt ?? item.createdAt) : 0;
}

/** Like/play keys for the most-liked / most-played sorts (natives trail at -1). */
function itemLikes(item: GalleryItem): number {
  return item.kind === 'creation' ? item.likes : -1;
}
function itemPlays(item: GalleryItem): number {
  return item.kind === 'creation' ? item.plays : -1;
}

/** A creation item's last-PLAYED key. A never-played creation sits at 0, so the
 *  tab reads top-down as "what people have been playing" with the untouched
 *  games behind it — and natives trail at -1 below even those, exactly as under
 *  every other sort (a hand-written platform game is a catalog entry, not fresh
 *  activity). */
function itemLastPlayed(item: GalleryItem): number {
  return item.kind === 'creation' ? item.lastPlayed : -1;
}

/**
 * A creation item's RATING key. An unrated game ranks at the neutral score
 * rather than at 0 — not having been rated is the platform's backlog, not the
 * creator's failing, and burying a brand-new game for it would be a bug that
 * looks like a policy. Natives trail at -1 as under every other sort.
 */
function itemRating(item: GalleryItem): number {
  if (item.kind !== 'creation') return -1;
  return item.rating?.score ?? NEUTRAL_APPEAL;
}

/**
 * Games the "Top rated" view drops entirely: the ones the rendered audit found
 * UNPLAYABLE — controls dead at the source, or a bot drive that moved nothing.
 *
 * Demote, not hide: this predicate is scoped to the top-rated view. The game
 * stays in every other sort, on its own page, and in its creator's studio —
 * nothing is unlisted and nobody's game disappears from the platform because a
 * probe failed. Reads the blocker list, never `score === 0`, so a game that
 * merely scored badly is never mistaken for a broken one.
 */
export function isDemoted(item: GalleryItem): boolean {
  return item.kind === 'creation' && isBlocked(item.rating);
}

/** True when a card should carry the quality mark. Never fires on an unrated
 *  or unaudited game — the mark is a claim, and we only make it when measured. */
export function isTopRated(item: GalleryItem): boolean {
  return item.kind === 'creation' && isShowcase(item.rating);
}

/**
 * How many versions a game has been through — the same number its card shows as
 * "vN", so the order matches what the reader can see. Natives trail at -1: a
 * hand-written platform game has no build history to iterate.
 */
function itemVersion(item: GalleryItem): number {
  return item.kind === 'creation' ? item.version : -1;
}

/**
 * Build the ordered, filtered gallery list from creations + native games.
 *
 * @param creations creation items (already projected)
 * @param natives   native-game items (already projected)
 * @param filter    style/creator/search/sort/includeNative
 * @param now       reference clock for trending (injected for determinism)
 */
export function buildGallery(
  creations: GalleryItem[],
  natives: GalleryItem[],
  filter: GalleryFilter = {},
  now: number = Date.now(),
): GalleryItem[] {
  const includeNative = filter.includeNative ?? true;
  // The PUBLIC gallery shows every PLAYABLE creation — an active version with a
  // bundle — regardless of build PROGRESS. A live game iterating a new version
  // (`buildState:'building'`, still `status:'live'`) stays in the grid; drafts
  // and never-built (truly failed) creations are not playable and drop out.
  // (Studio "My games" + Admin "All creations" render creations directly, not
  // through buildGallery, so they still see every creation.)
  const liveCreations = creations.filter((it) => it.kind !== 'creation' || it.playable);
  const pool = includeNative ? [...liveCreations, ...natives] : [...liveCreations];
  const sort = filter.sort ?? 'trending';
  // "Top rated" is the one view that DROPS games: a game the audit found
  // unplayable does not belong in a ranking of the best. It keeps its place
  // under every other sort — this is a demotion, not an unlisting.
  const filtered = pool
    .filter((it) => (sort === 'rated' ? !isDemoted(it) : true))
    .filter((it) => matchesFilter(it, filter));

  const keys: Record<GallerySort, (it: GalleryItem) => number> = {
    trending: (it) => itemScore(it, now),
    newest: itemRecency,
    created: itemCreated,
    liked: itemLikes,
    played: itemPlays,
    lastPlayed: itemLastPlayed,
    rated: itemRating,
    iterated: itemVersion,
  };
  const key = keys[sort];
  filtered.sort((a, b) => {
    const d = key(b) - key(a);
    if (d !== 0) return d;
    return a.title.localeCompare(b.title);
  });
  return filtered;
}

/**
 * The pinned "Featured" row above the main gallery grid: the creations named by
 * `platformSettings.galleryFeaturedSlugs`, in the slugs' order. Reuses
 * {@link buildGallery} (creations only, no filter) so the row is gated by the
 * SAME playable predicate as the grid — a slug that is unknown or not playable
 * resolves to nothing and is skipped. Empty result = the row is not rendered.
 */
export function featuredItems(creations: GalleryItem[], slugs: string[]): GalleryItem[] {
  if (slugs.length === 0) return [];
  const live = new Map(
    buildGallery(creations, [], { includeNative: false })
      // A hand-curated slug is still not allowed to put an UNPLAYABLE game in
      // the most prominent row on the platform — curation predates the rating,
      // and a pin made months ago should not survive the game breaking.
      .filter((it) => !isDemoted(it))
      .map((it) => [it.slug, it]),
  );
  return slugs
    .map((slug) => live.get(slug))
    .filter((it): it is GalleryItem => it !== undefined);
}

/** Shelf length for the front page's ranked discovery shelves — two visible
 *  rows of four with one screen of scroll headroom. */
const SHELF_SIZE = 12;

/**
 * The front page's three ranked discovery shelves (🏆 Top Rated / 💖 Most
 * Liked / ✨ Most Recent) — each a thin slice of {@link buildGallery}
 * (creations only), so every shelf inherits the playable gate, the `rated`
 * demotion filter, and the shared comparator exactly like
 * {@link featuredItems} does. No shelf-local ranking exists: if a richer
 * tiebreak is ever wanted, upgrade `buildGallery`'s comparator once so the
 * sort tabs and the shelves change together.
 */
function shelfItems(creations: GalleryItem[], sort: GallerySort): GalleryItem[] {
  return buildGallery(creations, [], { sort, includeNative: false }).slice(0, SHELF_SIZE);
}

/** 🏆 Top Rated — the quality-rating order; audit-blocked games drop out. */
export function topRatedShelfItems(creations: GalleryItem[]): GalleryItem[] {
  return shelfItems(creations, 'rated');
}

/** 💖 Most Liked. */
export function mostLikedShelfItems(creations: GalleryItem[]): GalleryItem[] {
  return shelfItems(creations, 'liked');
}

/** ✨ Most Recent — the first-PUBLISHED order (`created`), not last-updated. */
export function mostRecentShelfItems(creations: GalleryItem[]): GalleryItem[] {
  return shelfItems(creations, 'created');
}

/**
 * The pinned "Editors' Pick" row: the games in the CURRENT active list (stamped
 * by the pickEditorsPicks reviewer), ordered by `currentRank`. Pass the FULL
 * item pool (creations + natives) — natives are eligible picks too. A creation
 * must still be playable (a failed iteration since the last run drops out) and
 * must not be audit-BLOCKED; natives are evergreen catalog entries, always
 * shown. Derived purely from the
 * already-subscribed docs — no extra query. Empty = row not rendered.
 */
export function editorsPickItems(items: GalleryItem[]): GalleryItem[] {
  return items
    .filter((it) => {
      if (!it.editorsPick?.current) return false;
      // A laurel is not a licence: the reviewer scored a text dossier and never
      // saw the game run, so a pick whose build the audit found unplayable is
      // dropped here. (Two of the live picks were in exactly that state when
      // the rating shipped.)
      if (isDemoted(it)) return false;
      return it.kind === 'native' ? true : it.playable;
    })
    .sort((a, b) => (a.editorsPick?.currentRank ?? 999) - (b.editorsPick?.currentRank ?? 999));
}

/** All distinct creator handles present across the items (for the filter UI). */
export function creatorHandles(items: GalleryItem[]): string[] {
  const set = new Set<string>();
  for (const it of items) set.add(it.creatorHandle);
  return [...set].sort((a, b) => a.localeCompare(b));
}

/** The full ordered style list for the style-chip filter — re-exported from
 *  the spec registry (the single source; the functions' validation imports
 *  the same list). */
export { CREATION_STYLES as STYLES } from '@djgg/creations';
